import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);
const SESSION_MAX_AGE_MS = 60 * 24 * 60 * 60 * 1000;
const SESSION_IDLE_TIMEOUT_MS = 30 * 24 * 60 * 60 * 1000;
const SESSION_ACTIVITY_KEY = 'n369_auth_last_activity';
const SESSION_ISSUED_KEY = 'n369_auth_issued_at';
const HAS_SESSION_KEY = 'n369_has_session';

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const markActivity = useCallback(() => {
    window.localStorage.setItem(SESSION_ACTIVITY_KEY, Date.now().toString());
  }, []);

  const sessionExpired = useCallback(() => {
    const issuedAt = Number(window.localStorage.getItem(SESSION_ISSUED_KEY) || 0);
    const lastActivity = Number(window.localStorage.getItem(SESSION_ACTIVITY_KEY) || 0);
    const now = Date.now();
    if (!issuedAt || now - issuedAt > SESSION_MAX_AGE_MS) return true;
    if (!lastActivity || now - lastActivity > SESSION_IDLE_TIMEOUT_MS) return true;
    return false;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (error) {
      console.error('Logout error', error);
    }
    window.localStorage.removeItem(SESSION_ISSUED_KEY);
    window.localStorage.removeItem(SESSION_ACTIVITY_KEY);
    window.localStorage.removeItem(HAS_SESSION_KEY);
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const syncSession = async () => {
      if (window.location.hash?.includes('session_id=')) {
        setLoading(false);
        return;
      }
      if (!window.localStorage.getItem(HAS_SESSION_KEY)) {
        setLoading(false);
        return;
      }
      if (window.localStorage.getItem(SESSION_ISSUED_KEY) && sessionExpired()) {
        await logout();
        setLoading(false);
        return;
      }
      try {
        let response = await fetch(`${API}/auth/me`, { credentials: 'include' });
        if (!response.ok) {
          const refresh = await fetch(`${API}/auth/refresh`, { method: 'POST', credentials: 'include' });
          if (refresh.ok) {
            const refreshed = await refresh.json();
            if (refreshed.token) setToken(refreshed.token);
            response = await fetch(`${API}/auth/me`, { credentials: 'include' });
          }
        }
        if (!response.ok) throw new Error('No authenticated session');
        const data = await response.json();
        setUser(data.user);
        markActivity();
        window.localStorage.setItem(HAS_SESSION_KEY, '1');
        if (!window.localStorage.getItem(SESSION_ISSUED_KEY)) {
          window.localStorage.setItem(SESSION_ISSUED_KEY, Date.now().toString());
          window.localStorage.setItem(SESSION_ACTIVITY_KEY, Date.now().toString());
        }
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    syncSession();
  }, [logout, markActivity, sessionExpired]);

  useEffect(() => {
    if (!user) return undefined;

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    const handleActivity = () => markActivity();
    events.forEach((eventName) => window.addEventListener(eventName, handleActivity, { passive: true }));

    const interval = window.setInterval(() => {
      if (sessionExpired()) logout();
    }, 60000);

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, handleActivity));
      window.clearInterval(interval);
    };
  }, [user, logout, markActivity, sessionExpired]);

  const login = useCallback(async (email, password, otp) => {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email: email.trim().toLowerCase(), password, otp: otp || undefined })
    });
    const data = await res.json();
    if (!res.ok) {
      if (res.status === 401 && data.detail === '2FA_REQUIRED') {
        const err = new Error('2FA_REQUIRED');
        err.twofaRequired = true;
        throw err;
      }
      throw new Error(data.detail || 'Error de autenticación');
    }
    window.localStorage.setItem(SESSION_ISSUED_KEY, Date.now().toString());
    window.localStorage.setItem(SESSION_ACTIVITY_KEY, Date.now().toString());
    window.localStorage.setItem(HAS_SESSION_KEY, '1');
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const authFetch = useCallback(async (url, options = {}) => {
    const res = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: { ...options.headers, ...(token ? { Authorization: `Bearer ${token}` } : {}) }
    });
    if (res.ok) markActivity();
    if (res.status === 401) {
      try {
        const refresh = await fetch(`${API}/auth/refresh`, { method: 'POST', credentials: 'include' });
        if (refresh.ok) {
          const refreshed = await refresh.json();
          if (refreshed.token) setToken(refreshed.token);
          return fetch(url, {
            ...options,
            credentials: 'include',
            headers: { ...options.headers, ...(refreshed.token ? { Authorization: `Bearer ${refreshed.token}` } : {}) }
          });
        }
      } catch (error) {
        console.error('Refresh error', error);
      }
      await logout();
      throw new Error('Sesión expirada');
    }
    return res;
  }, [logout, markActivity, token]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, authFetch }}>
      {children}
    </AuthContext.Provider>
  );
};
